import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Handshake, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui";
import { ListSkeleton } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMyCollaborations,
  type Collaboration,
  type CollaborationStatus,
} from "@/hooks/useCollaborations";
import TurnBadge from "./components/TurnBadge";
import { COLLABORATION_TONE } from "./statusTone";

const OPEN_STATUSES: CollaborationStatus[] = [
  "NEGOTIATING",
  "AGREED",
  "FUNDED",
  "SHIPPED",
  "IN_PROGRESS",
  "SUBMITTED",
  "REVISION_REQUESTED",
  "APPROVED",
];

type Tab = "mine" | "open" | "all";

/**
 * Every collaboration the caller is a party to, sorted so their own turn is at
 * the top.
 *
 * The turn column is the reason this screen gets opened daily. An agency's real
 * problem is not that they have nowhere to talk — it is that campaign state
 * lives in one person's head and gets reconstructed by hand whenever the client
 * asks how things are going.
 */
export default function CollaborationsInbox() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("open");
  const [campaignId, setCampaignId] = useState<string | null>(null);

  const filter = useMemo(() => {
    if (tab === "mine") return { awaitingMe: true, campaignId };
    if (tab === "open") return { status: OPEN_STATUSES, campaignId };
    return { campaignId };
  }, [tab, campaignId]);

  const { collaborations, loading } = useMyCollaborations(filter);

  // Campaign filter options come from what is actually in the list, so it never
  // offers a campaign with nothing behind it.
  const campaigns = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of collaborations) {
      if (c.campaign) seen.set(c.campaign.id, c.campaign.title);
    }
    return [...seen.entries()];
  }, [collaborations]);

  const sorted = useMemo(() => {
    return [...collaborations].sort((a, b) => {
      const aMine = a.turnOwner === a.viewerParty ? 0 : 1;
      const bMine = b.turnOwner === b.viewerParty ? 0 : 1;
      if (aMine !== bMine) return aMine - bMine;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [collaborations]);

  const isCreator = user?.marketplaceRole === "CREATOR";

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-neutral-900">
          {t("marketplace.collaboration.inbox.title")}
        </h1>
        <p className="text-sm text-neutral-500 mt-0.5">
          {isCreator
            ? t("marketplace.collaboration.inbox.subtitleCreator")
            : t("marketplace.collaboration.inbox.subtitleBrand")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(["mine", "open", "all"] as Tab[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`px-3 py-1.5 rounded-control text-xs font-medium transition-colors cursor-pointer ${
              tab === value
                ? "bg-steel-600/5 text-steel-600 border border-steel-600/20"
                : "text-neutral-500 border border-neutral-200 hover:bg-neutral-50"
            }`}
          >
            {t(`marketplace.collaboration.inbox.tab.${value}`)}
          </button>
        ))}

        {campaigns.length > 1 && (
          <select
            value={campaignId ?? ""}
            onChange={(e) => setCampaignId(e.target.value || null)}
            className="ml-auto px-3 py-1.5 rounded-control border border-neutral-200 text-xs text-neutral-600 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-steel-600/20"
          >
            <option value="">{t("marketplace.collaboration.inbox.allCampaigns")}</option>
            {campaigns.map(([id, title]) => (
              <option key={id} value={id}>
                {title}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading && collaborations.length === 0 ? (
        <ListSkeleton />
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-card p-12 text-center">
          <div className="w-12 h-12 rounded-card bg-neutral-100 text-neutral-400 flex items-center justify-center mx-auto mb-4">
            <Handshake className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-neutral-900">
            {tab === "mine"
              ? t("marketplace.collaboration.inbox.emptyMine")
              : t("marketplace.collaboration.inbox.empty")}
          </p>
          <p className="text-sm text-neutral-500 mt-1">
            {isCreator
              ? t("marketplace.collaboration.inbox.emptyHintCreator")
              : t("marketplace.collaboration.inbox.emptyHintBrand")}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {sorted.map((collaboration) => (
            <CollaborationRow key={collaboration.id} collaboration={collaboration} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CollaborationRow({ collaboration }: { collaboration: Collaboration }) {
  const { t } = useTranslation();

  // Each side sees the other, never itself.
  const counterparty =
    collaboration.viewerParty === "BRAND" ? collaboration.creator : collaboration.brand;

  const rate = collaboration.money.agreedRate ?? collaboration.pendingOffer?.amount ?? null;
  const rateIsAgreed = collaboration.money.agreedRate !== null;

  const nextDue = collaboration.dueDate;

  return (
    <li>
      <Link
        to={`/colaboraciones/${collaboration.id}`}
        className="block bg-white border border-neutral-200 rounded-card p-4 hover:border-steel-600/30 transition-colors"
      >
        <div className="flex items-start gap-3">
          {counterparty.logoUrl ? (
            <img
              src={counterparty.logoUrl}
              alt=""
              className="w-10 h-10 rounded-control object-cover flex-shrink-0"
            />
          ) : (
            <span className="w-10 h-10 rounded-control bg-neutral-100 text-neutral-400 flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {counterparty.name.charAt(0).toUpperCase()}
            </span>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900 truncate">
                  {counterparty.handle ? `@${counterparty.handle}` : counterparty.name}
                </p>
                <p className="text-xs text-neutral-500 truncate">
                  {collaboration.campaign?.title ?? "—"}
                </p>
              </div>

              <TurnBadge
                turnOwner={collaboration.turnOwner}
                viewerParty={collaboration.viewerParty}
              />
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
              <Badge variant={COLLABORATION_TONE[collaboration.status] ?? "neutral"} stamp>
                {t(`marketplace.collaboration.status.${collaboration.status}`)}
              </Badge>

              {rate !== null && (
                <span className="text-xs text-neutral-600 tabular-nums">
                  {collaboration.money.currency} {rate.toLocaleString("es")}
                  {!rateIsAgreed && (
                    <span className="text-neutral-400">
                      {" "}
                      · {t("marketplace.collaboration.inbox.latestOffer")}
                    </span>
                  )}
                </span>
              )}

              {nextDue && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                  <Calendar className="w-3 h-3" />
                  {new Date(nextDue).toLocaleDateString("es", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              )}

              {collaboration.unreadCount > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-steel-600">
                  <MessageCircle className="w-3 h-3" />
                  {collaboration.unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}
